import { DOCUMENT } from '@angular/common';
import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs';

import { GlobalLoaderComponent } from '../shared/components/global-loader/global-loader.component';
import { PortalFooterComponent } from '../shared/components/layout/portal-footer/portal-footer.component';
import { PortalHeaderComponent } from '../shared/components/layout/portal-header/portal-header.component';
import { PortalSidebarComponent } from '../shared/components/layout/portal-sidebar/portal-sidebar.component';
import { ToastContainerComponent } from '../shared/components/toast-container/toast-container.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    GlobalLoaderComponent,
    PortalHeaderComponent,
    PortalSidebarComponent,
    PortalFooterComponent,
    ToastContainerComponent,
  ],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss',
})
export class AppComponent {
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);

  readonly sidebarCollapsed = signal(false);

  readonly isAuthRoute = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => this.isAuthUrl(event.urlAfterRedirects)),
      startWith(this.isAuthUrl(this.initialUrl())),
    ),
    { initialValue: this.isAuthUrl(this.initialUrl()) },
  );

  readonly isLandingRoute = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => this.isLandingUrl(event.urlAfterRedirects)),
      startWith(this.isLandingUrl(this.initialUrl())),
    ),
    { initialValue: this.isLandingUrl(this.initialUrl()) },
  );

  private isAuthUrl(url: string): boolean {
    return url.split('?')[0].startsWith('/auth');
  }

  private isLandingUrl(url: string): boolean { return ['/','/auth/home'].includes(url.split('?')[0]); }

  private initialUrl(): string {
    return this.router.url && this.router.url !== '/'
      ? this.router.url
      : `${this.document.location.pathname}${this.document.location.search}`;
  }

  expandSidebar(): void {
    this.sidebarCollapsed.set(false);
  }

  toggleSidebar(): void {
    this.sidebarCollapsed.update((collapsed) => !collapsed);
  }
}
